import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Building2, Trash2, RefreshCw, Loader2, Plus, Sparkles, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { API_BASE as API } from '../lib/apiBase';

const headers = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const jheaders = () => ({ 'Content-Type': 'application/json', ...headers() });

export default function DepartmentsManager() {
  const [departments, setDepartments] = useState([]);
  const [entities, setEntities] = useState([]);
  const [employeeCounts, setEmployeeCounts] = useState({}); // dept_name -> count
  const [filterEntity, setFilterEntity] = useState('all');
  const [loading, setLoading] = useState(false);
  const [newDept, setNewDept] = useState({ name: '', code: '', entity_id: '' });
  const [adding, setAdding] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', code: '', entity_id: '' });
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, eRes, empRes] = await Promise.all([
        fetch(`${API}/api/departments${filterEntity !== 'all' ? `?entity_id=${filterEntity}` : ''}`, { headers: headers() }),
        fetch(`${API}/api/entities`, { headers: headers() }),
        fetch(`${API}/api/employees`, { headers: headers() }),
      ]);
      if (dRes.ok) setDepartments(await dRes.json());
      if (eRes.ok) {
        const list = await eRes.json();
        list.sort((a, b) => ((a.level || 0) - (b.level || 0)) || (a.name || '').localeCompare(b.name || ''));
        setEntities(list);
      }
      if (empRes.ok) {
        const emps = await empRes.json();
        const list = Array.isArray(emps) ? emps : (emps.data || []);
        const counts = {};
        list.forEach((e) => {
          if (e.is_active === false) return;
          const d = (e.department || '').trim();
          if (d) counts[d] = (counts[d] || 0) + 1;
        });
        setEmployeeCounts(counts);
      }
    } catch (e) {
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  }, [filterEntity]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const entityName = (id) => (entities.find((e) => e.entity_id === id) || {}).name || '—';

  const addDept = async () => {
    if (!newDept.name.trim()) { toast.error('Department name is required'); return; }
    setAdding(true);
    try {
      const payload = {
        name: newDept.name.trim(),
        code: newDept.code.trim() || newDept.name.trim().toUpperCase().slice(0, 6),
        entity_id: newDept.entity_id || null,
      };
      const res = await fetch(`${API}/api/departments`, { method: 'POST', headers: jheaders(), body: JSON.stringify(payload) });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to add');
      }
      toast.success('Department added');
      setNewDept({ name: '', code: '', entity_id: '' });
      await fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAdding(false);
    }
  };

  const deleteDept = async (dept) => {
    const count = employeeCounts[dept.name] || 0;
    const msg = count > 0
      ? `${count} active employee(s) are still assigned to "${dept.name}". Delete anyway (force)?`
      : `Delete department "${dept.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      const res = await fetch(`${API}/api/departments/${dept.department_id}${count > 0 ? '?force=true' : ''}`, {
        method: 'DELETE', headers: headers(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Delete failed');
      toast.success(`Deleted "${dept.name}"`);
      await fetchAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const openEdit = (dept) => {
    setEditDept(dept);
    setEditForm({ name: dept.name || '', code: dept.code || '', entity_id: dept.entity_id || '' });
  };

  const saveEdit = async () => {
    if (!editDept) return;
    if (!editForm.name.trim()) { toast.error('Department name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        code: editForm.code.trim().toUpperCase(),
        entity_id: editForm.entity_id || null,
      };
      const res = await fetch(`${API}/api/departments/${editDept.department_id}`, {
        method: 'PUT', headers: jheaders(), body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Update failed');
      const renamed = data.employees_renamed || 0;
      toast.success(renamed > 0
        ? `Updated. ${renamed} employee record${renamed === 1 ? '' : 's'} also renamed.`
        : 'Department updated');
      setEditDept(null);
      await fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const cleanupUnused = async () => {
    const unusedCount = departments.filter((d) => (employeeCounts[d.name] || 0) === 0).length;
    if (unusedCount === 0) { toast.message('No unused departments found.'); return; }
    if (!window.confirm(`Delete ${unusedCount} department(s) that have ZERO active employees?`)) return;
    try {
      const qs = filterEntity !== 'all' ? `?entity_id=${filterEntity}` : '';
      const res = await fetch(`${API}/api/departments/cleanup-unused${qs}`, { method: 'POST', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Cleanup failed');
      toast.success(`Deleted ${data.deleted} unused departments`);
      await fetchAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="departments-manager">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 className="h-5 w-5 text-blue-600" /> Department Management
          </CardTitle>
          <p className="text-xs text-slate-500">
            Add, delete and consolidate departments per institute. Departments with zero active employees are safe to delete.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add new */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end border rounded-md p-3 bg-slate-50 dark:bg-slate-900">
            <div>
              <Label className="text-xs">New Department *</Label>
              <Input placeholder="e.g. Mechanical Engineering" value={newDept.name} onChange={(e) => setNewDept({ ...newDept, name: e.target.value })} data-testid="dept-new-name" />
            </div>
            <div>
              <Label className="text-xs">Code</Label>
              <Input placeholder="MECH" value={newDept.code} onChange={(e) => setNewDept({ ...newDept, code: e.target.value.toUpperCase() })} data-testid="dept-new-code" />
            </div>
            <div>
              <Label className="text-xs">Institute / Branch</Label>
              <Select value={newDept.entity_id || 'NONE'} onValueChange={(v) => setNewDept({ ...newDept, entity_id: v === 'NONE' ? '' : v })}>
                <SelectTrigger data-testid="dept-new-entity"><SelectValue placeholder="— none —" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="NONE">— None —</SelectItem>
                  {entities.map((e) => (
                    <SelectItem key={e.entity_id} value={e.entity_id}>
                      {'\u00A0\u00A0'.repeat(Math.max(0, e.level || 0))}{e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={addDept} disabled={adding} data-testid="dept-add-btn" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white">
              {adding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Add Department
            </Button>
          </div>

          {/* Filter + Cleanup */}
          <div className="flex flex-wrap gap-2 items-end">
            <div className="min-w-[220px]">
              <Label className="text-xs">Filter by Institute</Label>
              <Select value={filterEntity} onValueChange={setFilterEntity}>
                <SelectTrigger data-testid="dept-filter-entity"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All Institutes</SelectItem>
                  {entities.map((e) => (
                    <SelectItem key={e.entity_id} value={e.entity_id}>
                      {'\u00A0\u00A0'.repeat(Math.max(0, e.level || 0))}{e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={fetchAll} disabled={loading} data-testid="dept-refresh">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant="outline" className="text-amber-700 border-amber-300" onClick={cleanupUnused} data-testid="dept-cleanup-unused">
              <Sparkles className="h-4 w-4 mr-1" /> Cleanup Unused
            </Button>
          </div>

          {/* List */}
          <div className="border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 dark:bg-slate-900">
                  <TableHead>Department</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Institute</TableHead>
                  <TableHead className="text-right">Active Staff</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {departments.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-400 py-6">No departments found</TableCell></TableRow>
                ) : departments.map((d) => {
                  const cnt = employeeCounts[d.name] || 0;
                  return (
                    <TableRow key={d.department_id} data-testid={`dept-row-${d.department_id}`}>
                      <TableCell className="font-medium">{d.name}</TableCell>
                      <TableCell><Badge variant="outline">{d.code}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-500">{entityName(d.entity_id)}</TableCell>
                      <TableCell className="text-right">
                        {cnt === 0 ? <Badge className="bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300">Unused</Badge> : <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">{cnt}</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" className="text-blue-600" onClick={() => openEdit(d)} data-testid={`dept-edit-${d.department_id}`} title="Edit / rename">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteDept(d)} data-testid={`dept-delete-${d.department_id}`} title={cnt > 0 ? 'Force-delete (still in use)' : 'Hard delete'}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Department dialog */}
      <Dialog open={!!editDept} onOpenChange={(o) => { if (!o) setEditDept(null); }}>
        <DialogContent className="max-w-md" data-testid="dept-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-blue-600" /> Edit Department</DialogTitle>
            <DialogDescription>
              Renaming will also rename every existing employee currently in <strong>{editDept?.name}</strong> so payroll, leave-policy and reporting keep working.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Department Name *</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="dept-edit-name" />
            </div>
            <div>
              <Label className="text-xs">Code</Label>
              <Input value={editForm.code} onChange={(e) => setEditForm({ ...editForm, code: e.target.value.toUpperCase() })} data-testid="dept-edit-code" />
            </div>
            <div>
              <Label className="text-xs">Institute / Branch</Label>
              <Select value={editForm.entity_id || 'NONE'} onValueChange={(v) => setEditForm({ ...editForm, entity_id: v === 'NONE' ? '' : v })}>
                <SelectTrigger data-testid="dept-edit-entity"><SelectValue placeholder="— none —" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="NONE">— None —</SelectItem>
                  {entities.map((e) => (
                    <SelectItem key={e.entity_id} value={e.entity_id}>
                      {'\u00A0\u00A0'.repeat(Math.max(0, e.level || 0))}{e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {editDept && (employeeCounts[editDept.name] || 0) > 0 && (
              <div className="text-xs text-amber-700 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-900 p-2 rounded">
                {employeeCounts[editDept.name]} active employee(s) will be moved to the new name automatically.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDept(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="dept-edit-save" className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Pencil className="w-4 h-4 mr-1" />} Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
